import { useEffect, useMemo, useState } from "react";
import { useRemoveImageMutation, useUpdateCompanyByIdMutation } from "../../../../../../../API/authApi";
import useUploadFile from "../../../../../../../Components/FileUploader/fileUploader-hooks";
import { ImageTypes } from "../../../../../../../Interfaces/componentTypes";
import { IEditBusinessProfileMainProps } from "../ProfileMain/profileMainInfo";
import { useToastify } from "../../../../../../../Hooks/hooks";
import useLanguagesHooks from "../../../../../../../Components/Languages/setLanguage-hooks";
import { IUpdateCompanyById } from "../../../../../../../Interfaces/request-interfaces";

const useProfileGallery = (props: IEditBusinessProfileMainProps) => {
  const { data, onUpdate } = props;
  const [update, { data: updatedData, isSuccess: isUpdated, isError: isUpdateError, isLoading }] = useUpdateCompanyByIdMutation();
  const [removeImage] = useRemoveImageMutation();
  const { t } = useLanguagesHooks();
  const initData = useMemo(() => data.business_profile?.gallery ?? [], [data.business_profile?.gallery]);
  const { imageUrl, handleFileInputChange, ImageResponce, imageLoading } = useUploadFile(ImageTypes.GALLERY);
  const { showToastError, showToastSuccess } = useToastify();
  const [gallery, setGallery] = useState<string[]>(initData);
  const [isChanged, setIsChanged] = useState<boolean>(false);
  const [removingImage, setRemovingImage] = useState<string | null>(null);

  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const areArraysEqual = (arr1: string[], arr2: string[]) => {
      if (arr1.length !== arr2.length) return false;
      const sorted1 = [...arr1].sort();
      const sorted2 = [...arr2].sort();
      return sorted1.every((val, index) => val === sorted2[index]);
    };
    setIsChanged(!areArraysEqual(gallery, initData));
  }, [gallery, initData]);

  useEffect(() => {
    if (ImageResponce && imageUrl) {
      setGallery((prev) => {
        if (!prev.includes(ImageResponce.src!)) {
          return [...prev, ImageResponce.src!];
        }
        return prev;
      });
    }
  }, [ImageResponce, imageUrl]);

  const onRemoveImage = async (src: string) => {
    try {
      setRemovingImage(src);
      await removeImage({ image_src: src, company_id: data.id }).unwrap();
      setGallery((prev) => prev.filter((item) => item !== src));
    } catch (error) {
      showToastError(t('Toast.Error.Common'));
    } finally {
      setRemovingImage(null);
    }
  };

  useEffect(() => {
    if (isUpdated && updatedData) {
      onUpdate();
      showToastSuccess(t('Toast.Success.Common'));
      setIsChanged(false);
      scrollTop();
    } else if (isUpdateError) {
      showToastError(t('Toast.Error.Common'))
    }
  }, [isUpdated, updatedData, isUpdateError]);

  const onUpdateGallery = () => {
    const payload: IUpdateCompanyById = {
      id: data.id,
      data: {
        business_profile: {
          gallery: gallery,
        },
      },
    };
    update(payload);
  };

  return {
    gallery,
    isLoading,
    isChanged,
    imageLoading,
    removingImage,
    onRemoveImage,
    onUpdateGallery,
    handleFileInputChange,
  }
};

export default useProfileGallery